import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import Input from "../../../components/ui/Input";

const statusBadge = (status) => {
  switch (status) {
    case "approved":
      return "bg-success/15 text-success";
    case "rejected":
      return "bg-error/15 text-error";
    case "pending":
      return "bg-warning/15 text-warning";
    default:
      return "bg-muted text-text-secondary";
  }
};

const TimeOffManagement = ({
  timeOffRequests = [],
  staffMembers = [],
  loading = false,
  error = null,
  onCreate,
  onApprove,
  onReject,
  onViewDetails,
  defaultType = "vacation",
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    staffId: "",
    type: defaultType,
    startDate: "",
    endDate: "",
    reason: "",
    isPaid: true,
  });

  const typeLabel = (type) => {
    switch (type) {
      case "vacation": return t("staff.timeOffTypeVacation");
      case "sick": return t("staff.timeOffTypeSick");
      case "personal": return t("staff.timeOffTypePersonal");
      case "day_off": return t("staff.timeOffTypeDayOff");
      default: return t("staff.timeOffTypeOther");
    }
  };

  const statusOptions = [
    { value: "all", label: t("staff.timeOffFilterAll") },
    { value: "pending", label: t("staff.timeOffFilterPending") },
    { value: "approved", label: t("staff.timeOffFilterApproved") },
    { value: "rejected", label: t("staff.timeOffFilterRejected") },
  ];

  const sortOptions = [
    { value: "date", label: t("staff.timeOffSortDate") },
    { value: "start_date", label: t("staff.timeOffSortStartDate") },
    { value: "staff_name", label: t("staff.timeOffSortStaff") },
  ];

  const staffOptions = useMemo(() => {
    const opts = (staffMembers || []).map((s) => ({
      value: String(s.id),
      label: s.name || s.email || `#${s.id}`,
    }));
    return [{ value: "", label: t("staff.timeOffChooseStaff") }, ...opts];
  }, [staffMembers, t]);

  const typeOptions = [
    { value: "vacation", label: t("staff.timeOffTypeVacation") },
    { value: "sick", label: t("staff.timeOffTypeSick") },
    { value: "personal", label: t("staff.timeOffTypePersonal") },
    { value: "day_off", label: t("staff.timeOffTypeDayOff") },
    { value: "other", label: t("staff.timeOffTypeOther") },
  ];

  const filteredRequests = useMemo(() => {
    return (timeOffRequests || []).filter((r) => {
      if (filterStatus === "all") return true;
      return r?.status === filterStatus;
    });
  }, [timeOffRequests, filterStatus]);

  const sortedRequests = useMemo(() => {
    const list = [...filteredRequests];
    return list.sort((a, b) => {
      switch (sortBy) {
        case "date":
          return new Date(b.requestDate) - new Date(a.requestDate);
        case "start_date":
          return new Date(a.startDate) - new Date(b.startDate);
        case "staff_name":
          return String(a?.staffName || "").localeCompare(String(b?.staffName || ""));
        default:
          return 0;
      }
    });
  }, [filteredRequests, sortBy]);

  const resetForm = () =>
    setForm({ staffId: "", type: defaultType, startDate: "", endDate: "", reason: "", isPaid: true });

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!onCreate) return;
    if (!form.staffId) return alert(t("staff.timeOffChooseStaffAlert"));
    if (!form.startDate || !form.endDate) return alert(t("staff.timeOffChoosePeriodAlert"));

    setCreating(true);
    try {
      await onCreate({
        staff_id: Number(form.staffId),
        type: form.type,
        start_date: form.startDate,
        end_date: form.endDate,
        reason: form.reason,
        is_paid: form.isPaid,
      });
      setCreateOpen(false);
      resetForm();
    } finally {
      setCreating(false);
    }
  };

  const displayTitle = title ?? t("staff.timeOffTitle");
  const displaySubtitle = subtitle ?? t("staff.timeOffSubtitle");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{displayTitle}</h2>
          <p className="text-sm text-text-secondary">{displaySubtitle}</p>
        </div>

        <Button type="button" variant="primary" onClick={openCreate} className="shrink-0">
          <Icon name="Plus" size={16} className="mr-2" />
          {t("staff.timeOffNewRequest")}
        </Button>
      </div>

      <div className="bg-card rounded-lg border border-border p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            options={statusOptions}
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder={t("staff.timeOffFilterStatus")}
          />
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder={t("staff.timeOffSort")}
          />
          <div className="flex items-center justify-end text-sm text-text-secondary">
            {loading ? t("common.loading") : t("staff.timeOffRequests", { count: sortedRequests.length })}
          </div>
        </div>

        {error && (
          <div className="mt-3 text-sm text-error">{error}</div>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-text-secondary">{t("staff.timeOffLoading")}</div>
      ) : sortedRequests.length ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedRequests.map((request) => (
            <div
              key={request.id}
              className="bg-card rounded-lg border border-border p-4 hover-elevate transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Image
                    src={request.staffAvatar}
                    alt={request.staffName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{request.staffName}</div>
                    <div className="text-xs text-text-secondary flex items-center gap-1.5">
                      <span>{typeLabel(request.type)}</span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        request.isPaid
                          ? "bg-success/10 text-success"
                          : "bg-muted text-text-secondary"
                      }`}>
                        {request.isPaid ? t("staff.timeOffWithPay") : t("staff.timeOffWithoutPay")}
                      </span>
                    </div>
                  </div>
                </div>

                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(request.status)}`}
                >
                  {request.status === "pending"
                    ? t("staff.timeOffStatusPending")
                    : request.status === "approved"
                    ? t("staff.timeOffStatusApproved")
                    : request.status === "rejected"
                    ? t("staff.timeOffStatusRejected")
                    : request.status}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-text-secondary">
                  <Icon name="Calendar" size={16} />
                  <span>
                    {request.startDate} - {request.endDate}
                  </span>
                </div>

                {request.reason ? (
                  <div className="text-text-secondary line-clamp-2">{request.reason}</div>
                ) : null}

                <div className="text-xs text-text-secondary">{t("staff.timeOffRequestedOn")} {request.requestDate}</div>
              </div>

              <div className="flex items-center justify-between mt-4 gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onViewDetails?.(request)}
                >
                  {t("staff.timeOffDetails")}
                </Button>

                {request.status === "pending" ? (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onReject?.(request)}
                    >
                      {t("staff.timeOffReject")}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => onApprove?.(request)}
                    >
                      {t("staff.timeOffApprove")}
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-text-secondary">—</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-14 text-center">
          <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Calendar" size={22} className="text-text-secondary" />
          </div>
          <div className="text-lg font-medium text-foreground mb-2">{t("staff.timeOffNoRequests")}</div>
          <div className="text-sm text-text-secondary mb-4">
            {t("staff.timeOffNoRequestsDesc")}
          </div>
          <Button type="button" variant="outline" onClick={openCreate}>
            <Icon name="Plus" size={16} className="mr-2" />
            {t("staff.timeOffNewRequest")}
          </Button>
        </div>
      )}

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !creating && setCreateOpen(false)}
          />
          <div className="relative w-full max-w-lg bg-card border border-border rounded-2xl shadow-xl flex flex-col max-h-[85dvh]">
            <div className="flex items-center justify-between p-4 pb-3 shrink-0">
              <div className="text-lg font-semibold text-foreground">
                {defaultType === "day_off" ? t("staff.timeOffModalDayOff") : t("staff.timeOffModalTimeOff")}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => !creating && setCreateOpen(false)}
              >
                <Icon name="X" size={18} />
              </Button>
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 px-4 pb-2 flex flex-col gap-3">
              <Select
                options={staffOptions}
                value={form.staffId}
                onChange={(v) => setForm((p) => ({ ...p, staffId: v }))}
                placeholder={t("staff.timeOffChooseStaff")}
              />
              {defaultType === "day_off" ? null : (
                <Select
                  options={typeOptions}
                  value={form.type}
                  onChange={(v) => setForm((p) => ({ ...p, type: v }))}
                  placeholder={t("staff.timeOffType")}
                />
              )}

              {/* Paid / Unpaid toggle */}
              <div className="flex items-center gap-3 rounded-lg border border-border p-3 bg-surface">
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{t("staff.timeOffPaidLabel")}</div>
                  <div className="text-xs text-text-secondary">
                    {form.isPaid ? t("staff.timeOffPaidDesc") : t("staff.timeOffUnpaidDesc")}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isPaid}
                  onClick={() => setForm((p) => ({ ...p, isPaid: !p.isPaid }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    form.isPaid ? "bg-primary" : "bg-muted"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                      form.isPaid ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="date"
                  label="Start"
                  value={form.startDate}
                  onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                />
                <Input
                  type="date"
                  label="End"
                  value={form.endDate}
                  onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">{t("staff.timeOffReasonLabel")}</label>
                <textarea
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-foreground placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t("staff.timeOffReasonPlaceholder")}
                />
              </div>

            </div>
            <div className="flex items-center justify-end gap-2 p-4 pt-3 border-t border-border shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => !creating && setCreateOpen(false)}
                disabled={creating}
              >
                {t("staff.timeOffCancel")}
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={submitCreate}
                disabled={creating}
              >
                {creating ? t("staff.timeOffCreating") : t("staff.timeOffCreate")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeOffManagement;
